/**
 * Protected Route Component
 * Wraps routes that require authentication and/or specific permissions
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated, selectAuthLoading } from '../slices/authSlice';

/**
 * Protected Route Component
 * @param {Object} props
 * @param {string|string[]} props.requiredPermission - Required permission(s)
 * @param {string} props.redirectTo - Redirect path if unauthorized
 * @param {React.ReactNode} props.fallback - Loading fallback component
 */
function ProtectedRoute({ requiredPermission = null, redirectTo = '/login', fallback = null }) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectAuthLoading);
  const location = useLocation();

  // Get user permissions (always call useSelector at top level)
  const permissions = useSelector((state) => state.auth.permissions || []);

  // Check permission - calculate hasPermission based on permissions array
  // This avoids conditional hook calls
  const hasPermission = React.useMemo(() => {
    if (!requiredPermission) return true;

    // Explicit opt-in bypass for local dev. Set VITE_DISABLE_PERMISSION_CHECK=true in .env.local
    // Backend APIs still enforce authorization server-side.
    if (
      import.meta.env.VITE_DISABLE_PERMISSION_CHECK === 'true' &&
      (!permissions || permissions.length === 0)
    ) {
      return true;
    }

    // SECURITY FIX: Don't grant access if permissions are not yet loaded
    // Empty permissions means they haven't been decoded from JWT yet
    // This prevents unauthorized access during the permission loading race
    if (!permissions || permissions.length === 0) {
      // If we're still loading, return false and let isLoading fallback handle it
      // Only grant grace period if explicitly configured and loading is complete
      if (isLoading) {
        return false; // Will show loading state via fallback
      }
      // After loading is complete, true empty permissions = user has no permissions
      // On fresh signup, backend ensures at least admin gets 35+ permissions
      // On invitation, user gets role-specific permissions from backend
      return false; // Deny access until permissions are properly set
    }

    // Super admin has all permissions
    if (permissions.includes('super:admin')) return true;

    // Check specific permission(s)
    if (Array.isArray(requiredPermission)) {
      return requiredPermission.some((perm) => permissions.includes(perm));
    }
    return permissions.includes(requiredPermission);
  }, [requiredPermission, permissions, isLoading]);

  // Show loading fallback while checking auth
  if (isLoading && fallback) {
    return fallback;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Redirect to unauthorized page if lacking permission
  if (requiredPermission && !hasPermission) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  // Render protected content
  return <Outlet />;
}

ProtectedRoute.propTypes = {
  requiredPermission: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  redirectTo: PropTypes.string,
  fallback: PropTypes.node,
};

export default ProtectedRoute;
